import { Target, ShieldAlert, Eye, TrendingUp } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const BENEFITS = [
  { icon: Target, title: "Improved Data Accuracy", text: "Systematic checks against source records raise the reliability of every patent data field you depend on." },
  { icon: ShieldAlert, title: "Reduced Operational Risk", text: "Surface discrepancies early — before they propagate through docketing, prosecution, or portfolio decisions." },
  { icon: Eye, title: "Better Portfolio Visibility", text: "Know exactly which records are verified, which are inconsistent, and which need professional review." },
  { icon: TrendingUp, title: "Scalable Quality Control", text: "From a single matter to tens of thousands of records, the same disciplined methodology applies." },
];

export default function WhyMatters() {
  return (
    <section id="why-us" data-testid="why-us-section" className="bg-slate-50 py-24 lg:py-32 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel index="02">Why Certify IP Global</SectionLabel>
              <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-8">
                Why Accurate IP Data Matters
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-6">
                Inaccurate or incomplete patent data can create real operational challenges for
                law firms and corporate IP departments — from docketing errors to flawed
                portfolio decisions.
              </p>
              <p className="text-slate-600 text-base leading-relaxed mb-10">
                Reliable data helps teams make better decisions, maintain accurate portfolios,
                support prosecution workflows, and identify information that requires further
                professional review.
              </p>
              <div className="relative overflow-hidden border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3ODY4MDUwODN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern corporate environment representing professional IP operations"
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-navy/20" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200 self-start">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08} className="bg-white">
                <div data-testid={`benefit-card-${i + 1}`} className="p-8 lg:p-10 h-full hover:bg-navy group transition-colors duration-500">
                  <b.icon className="w-7 h-7 text-accent mb-6 group-hover:text-blue-400 transition-colors duration-500" strokeWidth={1.5} />
                  <h3 className="font-display font-bold text-navy text-lg mb-3 tracking-tight group-hover:text-white transition-colors duration-500">
                    {b.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-300 transition-colors duration-500">
                    {b.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
